import { useQuery } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import capitalize from 'lodash/capitalize';
import upperCase from 'lodash/upperCase';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import classNames from 'classnames';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Tooltip from '@atlaskit/tooltip';
import { Text } from '@atlaskit/primitives';
import {
  ColGroup,
  CopyableText,
  CustomDynamicResultTable,
  CustomizeView,
  FormattedAddress,
  FormattedDate,
  HeadCell,
  SearchResultColumn,
  TableBody,
  TableHead,
} from '@css-xp/base';
import {
  FetchPartnerProductEntitlementsQuery,
  FetchPartnerProductEntitlementsQueryVariables,
  InvoiceBillAndShipTo,
  TransactionAccountProduct,
  PartnerSearchContext,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { AppsCountCell } from '@css-xp/ppc-subscription-table/src/components/apps-count';
import { AddOnsCountCell } from '@css-xp/ppc-subscription-table/src/components/add-ons-count-cell';
import {
  BillingProfileNameFromId,
  CloudSiteLink,
  emptyStateSearchTabs,
  errorStateProp,
  getSelectedColumns,
  NoData,
  OrgNameLink,
  PPC_TABS,
  SoldToAddressDetailsLink,
  TableHeading,
  Usage,
} from '@css-xp/ppc-common';
import { CloudProductDetailView, isActiveSubscription } from '@css-xp/base/entitlement-utils';
import {
  DEFAULT_PAGE_SIZE,
  SEARCH_PAGE_LOADER_OPTIONS,
  SUBSCRIPTION_CONSTANTS,
  routePaths,
} from '@css-xp/ppc-constants';
import {
  getRouteUrl,
  getChargeElementV2,
  isBilledElsewhereProduct,
  splitAndCapitalize,
  isOrgProduct,
} from '@css-xp/ppc-utils';
import { getStatusForProductSubscription, LastInvoiceAmountCell } from '@css-xp/ppc-subscription-table';
import { emptyStatePropForWrongSearch } from '@css-xp/ppc-common/src/state-props';
import { fetchPartnerProductEntitlements } from './queries/fetch-partner-product-entitlements';
import './subscription-search.scss';

interface SubscriptionsSearchProps {
  query: string;
  transactionAccountId: string;
  partnerSearchContext: PartnerSearchContext;
  isDesignIteration1Enabled?: boolean;
  isAddOnsEnabled?: boolean;
  isSupportForExternallyBilledSubscriptionsEnabled?: boolean;
  columnsToShow?: string[];
  version?: number;
  isSandboxSupportForSubscriptionsEnabled?: boolean;
  isSubscriptionUsedForInternalUsageLicense?: boolean;
  isNonCommercialPricingEnabled?: boolean;
}

type ColumnList = HeadCell & SearchResultColumn;

export const SubscriptionsSearch = ({
  query,
  transactionAccountId,
  partnerSearchContext,
  isDesignIteration1Enabled,
  isAddOnsEnabled,
  isSupportForExternallyBilledSubscriptionsEnabled = false,
  version,
  isSandboxSupportForSubscriptionsEnabled = false,
  isNonCommercialPricingEnabled = false,
}: SubscriptionsSearchProps) => {
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
  const [tableRows, setTableRows] = useState<Array<unknown>>([]);
  const [previousSearchContext, setPreviousSearchContext] = useState<PartnerSearchContext | undefined>(undefined);
  const [tableLevelLoading, setTableLevelLoading] = useState<boolean>(true);
  const subscriptionsRoutePPC =
    transactionAccountId && getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });
  const { data, loading, refetch } = useQuery<
    FetchPartnerProductEntitlementsQuery,
    FetchPartnerProductEntitlementsQueryVariables
  >(fetchPartnerProductEntitlements, {
    variables: {
      pageParams: {
        size: DEFAULT_PAGE_SIZE,
      },
      searchParams: {
        query: query,
        partnerSearchContext: partnerSearchContext,
        transactionAccountId: transactionAccountId,
        isOrgSearchEnabled: true,
      },
      fetchIsExternallyEnabled: isSupportForExternallyBilledSubscriptionsEnabled,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-and-network',
  });

  const onLoadMore = useCallback(() => {
    if (endCursor) {
      setTableLevelLoading(false);
      setHasNextPage(false);
      refetch({
        pageParams: {
          size: DEFAULT_PAGE_SIZE,
          cursor: endCursor,
        },
      });
    }
  }, [refetch, endCursor]);

  // Once the search context is changed, need to remove load more button and enable table level loading
  useEffect(() => {
    if (loading) {
      setTableRows([]);
    }
    setHasNextPage(false);
    setTableLevelLoading(true);
  }, [partnerSearchContext]);

  useEffect(() => {
    if (!loading) {
      const nodes = data?.partnerProductEntitlements?.nodes ?? [];
      if (partnerSearchContext === previousSearchContext) {
        // This is for load more on same search context.
        setTableRows([...tableRows, ...nodes]);
      } else {
        setTableRows([...nodes]);
      }
      setHasNextPage(data?.partnerProductEntitlements?.pageInfo?.hasNextPage ?? false);
      setEndCursor(data?.partnerProductEntitlements?.pageInfo?.endCursor ?? undefined);
      setPreviousSearchContext(partnerSearchContext);
    }
  }, [data, loading]);

  const headersMap = () => {
    return {
      [SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION,
        customClassNames: 'col-subscription',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS,
        customClassNames: 'col-status',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING,
        customClassNames: 'col-plan',
        isMandatory: false,
        isChecked: true,
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.APPS]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.APPS,
        customClassNames: 'col-apps',
        ...(isDesignIteration1Enabled && {
          renderComponent: <th className="text-align-right">{SUBSCRIPTION_CONSTANTS.COLUMNS.APPS}</th>,
        }),
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS,
        customClassNames: 'col-add-ons',
        ...(isDesignIteration1Enabled && {
          renderComponent: <th className="text-align-right">{SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS}</th>,
        }),
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME,
        customClassNames: 'col-site',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.SITE]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.SITE,
        customClassNames: 'col-site',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY,
        customClassNames: 'col-common',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS,
        customClassNames: 'col-sold-to-address',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS,
        customClassNames: 'col-common',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON,
        customClassNames: 'col-common col-renew-date',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE,
        customClassNames: 'col-common',
        isMandatory: false,
        isChecked: true,
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT,
        customClassNames: 'col-common',
        isMandatory: false,
        isChecked: true,
        renderComponent: isDesignIteration1Enabled ? (
          <th className="text-align-right">{SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT}</th>
        ) : null,
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE,
        customClassNames: 'col-billing-profile',
      },
      [SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE]: {
        name: SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE,
        content: SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE,
        customClassNames: 'col-usage',
      },
    };
  };

  const getHeaders = useMemo(() => {
    const columnHeaders = headersMap();

    return [
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.APPS],
      ...(isAddOnsEnabled ? [columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS]] : []),
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.SITE],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY],
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS],
      ...(isDesignIteration1Enabled
        ? [columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE]]
        : [columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS]]),
      ...(isDesignIteration1Enabled ? [columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE]] : []),
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON],
      ...(isDesignIteration1Enabled ? [] : [columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE]]),
      columnHeaders[SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT],
    ];
  }, [isAddOnsEnabled]);

  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHeaders);

  const extractSiteUrlCell = (productSubscription: TransactionAccountProduct) => {
    const siteName = productSubscription?.cloudSiteHostname;
    return (
      <td>
        <CloudSiteLink
          siteName={siteName}
          txaId={transactionAccountId}
          cloudSiteId={productSubscription?.cloudSiteId}
          shouldRemoveLineClamp={isDesignIteration1Enabled}
          isOrgProductEntitlement={
            isDesignIteration1Enabled &&
            isOrgProduct({
              productIdOrKey: productSubscription?.productId,
              offeringName: productSubscription?.edition,
              relatesFromEntitlements: productSubscription?.relatesToEntitlements,
              offering: productSubscription?.offering,
            })
          }
        />
      </td>
    );
  };

  const getSoldToAddress = (
    shipTo: InvoiceBillAndShipTo | undefined | null,
    transactionAccountId: string,
    isDesignIteration1Enabled?: boolean
  ) => {
    if (!shipTo) {
      return <NoData testId={'sold-to-address-not-available'} />;
    }
    return (
      <SoldToAddressDetailsLink
        txaId={transactionAccountId}
        shipToId={shipTo?.id}
        shouldDisableLink={isDesignIteration1Enabled}
      >
        <span className="line-clamp">
          <FormattedAddress
            addressLine1={shipTo?.address?.address1}
            addressLine2={shipTo?.address?.address2}
            city={shipTo?.address?.city}
            country={shipTo?.address?.country}
            postcode={shipTo?.address?.postalCode}
            state={shipTo?.address?.state}
          />
        </span>
      </SoldToAddressDetailsLink>
    );
  };

  const getCompanyName = (shipTo: InvoiceBillAndShipTo | undefined | null, transactionAccountId: string) => {
    if (!shipTo || !shipTo.fullName) {
      return <NoData testId={'organization-not-available'} />;
    }
    return (
      <SoldToAddressDetailsLink txaId={transactionAccountId} companyName={shipTo.fullName}>
        <span className="line-clamp">
          <FormattedAddress organizationName={shipTo.fullName} />
        </span>
      </SoldToAddressDetailsLink>
    );
  };

  const getBilledUsers = (productSubscription: TransactionAccountProduct) => (
    <td>
      <div>
        {productSubscription?.billedUsers && (
          <div>
            {capitalize(
              getChargeElementV2({
                productId: productSubscription?.productId as string,
                edition: productSubscription?.edition as string,
                labelPlural: Number(productSubscription?.billedUsers) > 1,
                chargeElement: productSubscription?.chargeQuantities?.[0]?.chargeElement as string,
              })
            )}
          </div>
        )}
        <div>{productSubscription?.billedUsers ?? <NoData testId={'billed-users-not-available'} />}</div>
      </div>
    </td>
  );

  const getRenewOn = (productSubscription: TransactionAccountProduct) => {
    {
      // Currently, the backend data (billingStatus and provisioningStatus) is not transformed.
      // Hence, using the upperCase method.
      const billingStatus = upperCase(productSubscription?.billingStatus ?? '');
      const provisioningStatus = upperCase(productSubscription?.provisioningStatus ?? '');
      const renewInDays =
        productSubscription?.latestPeriodEndDate &&
        differenceInDays(new Date(productSubscription?.latestPeriodEndDate), new Date());
      const dayPrefix = typeof renewInDays === 'number' ? (renewInDays === 1 ? 'day' : 'days') : '';
      const shouldShowWarning = renewInDays && renewInDays < 8;

      return (
        <td>
          {productSubscription?.latestPeriodEndDate && isActiveSubscription(billingStatus, provisioningStatus) ? (
            <div>
              {shouldShowWarning && (
                <span className="warning-icon-container" data-testid="warning-icon-container">
                  <WarningIcon label="warning" primaryColor="orange" />{' '}
                </span>
              )}
              <FormattedDate date={productSubscription?.latestPeriodEndDate} format={NEW_DATE_FORMAT} />
              &nbsp;
              <Tooltip
                tag="span"
                content={
                  <>
                    in {renewInDays} {dayPrefix}
                  </>
                }
              >
                <InfoIcon label="info" size="small" />
              </Tooltip>
            </div>
          ) : (
            <NoData testId={'renew-in-not-available'} />
          )}
        </td>
      );
    }
  };

  const getBillingCycle = (productSubscription: TransactionAccountProduct) => (
    <td>
      {productSubscription?.billingFrequency ? (
        capitalize(productSubscription.billingFrequency)
      ) : (
        <NoData testId={'billing-cycle-not-available'} />
      )}
    </td>
  );

  const getRowData = (productSubscription: TransactionAccountProduct) => {
    const columns: (HeadCell & SearchResultColumn)[] = getSelectedColumns(columnsList);

    return columns.map((column) => {
      switch (column.name) {
        case SUBSCRIPTION_CONSTANTS.COLUMNS.SUBSCRIPTION:
          return (
            <td>
              <CloudProductDetailView
                productId={productSubscription?.productId as string}
                productName={productSubscription?.productName}
                productLink={`${subscriptionsRoutePPC}${productSubscription?.id}`}
                logoId={productSubscription?.logoId as string}
                version={version}
                billedElsewhere={isBilledElsewhereProduct(productSubscription?.productId)}
                {...(version === 2 && { entitlementNumber: productSubscription?.entitlementNumber ?? '' })}
                isSandbox={(isSandboxSupportForSubscriptionsEnabled && productSubscription?.isSandbox) ?? false}
                isAddOn={!!productSubscription?.isAddOn}
                isExternallyBilled={
                  isSupportForExternallyBilledSubscriptionsEnabled && Boolean(productSubscription?.isExternallyBilled)
                }
              />
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.STATUS:
          return getStatusForProductSubscription(productSubscription);
        case SUBSCRIPTION_CONSTANTS.COLUMNS.APPS:
          return <AppsCountCell subscription={productSubscription} baseRoute={subscriptionsRoutePPC} />;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.ADD_ONS:
          return <AddOnsCountCell subscription={productSubscription} baseRoute={subscriptionsRoutePPC} />;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.OFFERING:
          return (
            <td>
              {productSubscription?.offering?.name ?? productSubscription?.edition ?? <NoData />}
              {isNonCommercialPricingEnabled && (
                <Text size="small" color="color.text.subtlest" as="p">
                  {splitAndCapitalize(productSubscription?.pricingPlan?.type ?? '')}
                </Text>
              )}
              {version === 2 && (
                <Text size="small" color="color.text.subtlest" as="p">
                  {capitalize(productSubscription?.billingFrequency ?? '')}
                </Text>
              )}
            </td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.SITE:
          return extractSiteUrlCell(productSubscription);
        case SUBSCRIPTION_CONSTANTS.COLUMNS.COMPANY:
          return <td>{getCompanyName(productSubscription?.shipTo, transactionAccountId)}</td>;
        case SUBSCRIPTION_CONSTANTS.COLUMNS.SOLD_TO_ADDRESS:
          return (
            <td>{getSoldToAddress(productSubscription?.shipTo, transactionAccountId, isDesignIteration1Enabled)}</td>
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.BILLED_USERS:
          return getBilledUsers(productSubscription);
        case SUBSCRIPTION_CONSTANTS.COLUMNS.RENEW_ON:
          return getRenewOn(productSubscription);
        case SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_CYCLE:
          return getBillingCycle(productSubscription);
        case SUBSCRIPTION_CONSTANTS.COLUMNS.ORGANIZATION_NAME: {
          const { orgName, orgId } = productSubscription || {};

          return (
            <td>
              <OrgNameLink orgName={orgName} orgId={orgId} txaId={transactionAccountId} />
            </td>
          );
        }
        case SUBSCRIPTION_CONSTANTS.COLUMNS.BILLING_PROFILE: {
          return (
            <td>
              <BillingProfileNameFromId
                id={productSubscription?.billingGroupId}
                txaId={transactionAccountId || ''}
                showBillingProfileNumber={true}
              />
            </td>
          );
        }
        case SUBSCRIPTION_CONSTANTS.COLUMNS.LAST_INVOICE_AMOUNT:
          return (
            <LastInvoiceAmountCell
              entitlementId={productSubscription?.id}
              transactionAccountId={transactionAccountId || ''}
              isRightAligned={isDesignIteration1Enabled}
            />
          );
        case SUBSCRIPTION_CONSTANTS.COLUMNS.USAGE:
          return (
            <td>
              <Usage
                activeUsers={productSubscription?.activeUsers}
                userTier={productSubscription?.userTier}
                productId={productSubscription?.productId}
                edition={productSubscription?.edition}
                chargeElement={productSubscription?.chargeQuantities?.[0]?.chargeElement}
              />
            </td>
          );
      }
    });
  };

  const getRowsWithData = (subscriptionsData: any) => (
    <>
      <ColGroup columns={getSelectedColumns(columnsList)} />
      <TableHead columns={getSelectedColumns(columnsList)} />
      <TableBody>
        {subscriptionsData.map((productSubscription: any, index: number) => {
          return <tr key={productSubscription?.id ?? index.toString()}>{getRowData(productSubscription)}</tr>;
        })}
      </TableBody>
    </>
  );

  const customDynamicResultTableProps = {
    // children: getRowsWithData(tableRows),
    className: classNames('ppc-subscription-search-table', {
      // All sticky styles are to be handled in sticky-table-with-shadow class for old design
      'sticky-table-with-shadow': !isDesignIteration1Enabled,
      'ppc-subscription-search-table-v2': isDesignIteration1Enabled,
    }),
    customiseColumnView: isDesignIteration1Enabled ? (
      <></>
    ) : (
      <CustomizeView
        columns={columnsList}
        columnCount={columnsList.length}
        updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
      />
    ),
    noResults: tableRows.length === 0,
    isLoading: tableLevelLoading && loading,
    error: '',
    innerHeading: !isDesignIteration1Enabled && <TableHeading>{PPC_TABS.SUBSCRIPTIONS}</TableHeading>,
    testId: 'search-subscription-tab',
    emptyStateProps: isDesignIteration1Enabled ? emptyStatePropForWrongSearch : emptyStateSearchTabs,
    errorStateProps: errorStateProp,
    showLoadMore: hasNextPage,
    onLoadMore: onLoadMore,
    loadMoreLoading: !tableLevelLoading && loading,
    loaderOptions: isDesignIteration1Enabled ? SEARCH_PAGE_LOADER_OPTIONS : {},
    hasTableWrapper: true,
  };
  return (
    <CustomDynamicResultTable {...customDynamicResultTableProps}>{getRowsWithData(tableRows)}</CustomDynamicResultTable>
  );
};
